<template>
    <main class="flex-1">
        <div class="py-6">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <div class="flex justify-between items-center">
                    <h1 class="text-2xl font-semibold text-gray-900">Správa kategórií cien</h1>
                    <button @click="handleAddPriceCategory"
                        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                        Pridať kategóriu
                    </button>
                </div>
            </div>

            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <div class="py-4">
                    <!-- Loading state -->
                    <div v-if="loading" class="flex justify-center py-8">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                    </div>

                    <!-- Error message -->
                    <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                        <div class="text-sm text-red-700">{{ error }}</div>
                    </div>

                    <!-- Categories Table -->
                    <div v-if="!loading"
                        class="mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-300">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                                        Kategória
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                                        Počet cien
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                                        Podkategórie
                                    </th>
                                    <th scope="col" class="relative px-6 py-3">
                                        <span class="sr-only">Akcie</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200 bg-white">
                                <tr v-for="priceCategory in priceCategoriesWithDetails" :key="priceCategory.id"
                                    class="hover:bg-gray-50">
                                    <td class="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                                        {{ priceCategory.name }}
                                    </td>
                                    <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                        {{ priceCategory.priceCount }}
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-500">
                                        <div v-if="priceCategory.subcategories.length > 0" class="flex flex-wrap gap-1">
                                            <span v-for="subcategory in priceCategory.subcategories" :key="subcategory"
                                                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                {{ subcategory }}
                                            </span>
                                        </div>
                                        <span v-else class="text-gray-400 italic">Žiadne podkategórie</span>
                                    </td>
                                    <td
                                        class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                        <button @click="handleEditPriceCategory(priceCategory)"
                                            class="text-teal-600 hover:text-teal-900 mr-4">
                                            Upraviť
                                        </button> <button @click="handleDeletePriceCategory(priceCategory)"
                                            :disabled="priceCategory.priceCount > 0" :class="priceCategory.priceCount > 0
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-red-600 hover:text-red-900'" :title="priceCategory.priceCount > 0
                                                    ? `Nie je možné vymazať kategóriu s ${priceCategory.priceCount} cenami. Najprv vymaže alebo presuňte všetky ceny.`
                                                    : 'Vymazať kategóriu'">
                                            Vymazať
                                            <span v-if="priceCategory.priceCount > 0" class="ml-1">🔒</span>
                                        </button>
                                    </td>
                                </tr>
                                <!-- No categories message -->
                                <tr v-if="priceCategoriesWithDetails.length === 0">
                                    <td colspan="4" class="px-6 py-8 text-sm text-center text-gray-500">
                                        Žiadne kategórie neboli nájdené. Pridajte prvú kategóriu.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add/Edit Category Modal -->
        <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                <div class="mt-3">
                    <div class="flex items-center justify-between pb-3">
                        <h3 class="text-lg font-medium text-gray-900">
                            {{ editMode ? 'Upraviť kategóriu' : 'Pridať kategóriu' }}
                        </h3>
                        <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>

                    <form @submit.prevent="handleSubmit" class="space-y-4"> <!-- Category Name -->
                        <div>
                            <label for="categoryName" class="block text-sm font-medium text-gray-700">
                                Názov kategórie
                            </label>
                            <input type="text" id="categoryName" v-model="priceCategoryForm.name"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                placeholder="Zadajte názov kategórie" required />
                            <p class="mt-1 text-xs text-gray-500">
                                Názov kategórie musí byť unikátny.
                            </p>
                        </div>

                        <!-- Description -->
                        <div>
                            <label for="categoryDescription" class="block text-sm font-medium text-gray-700">
                                Popis (voliteľný)
                            </label>
                            <textarea id="categoryDescription" v-model="priceCategoryForm.description" rows="3"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                placeholder="Zadajte popis kategórie"></textarea>
                        </div>

                        <!-- Subcategories -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Podkategórie</label>
                            <!-- Add subcategory input -->
                            <div class="mt-2 flex gap-2">
                                <input type="text" v-model="newSubcategory"
                                    class="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                    placeholder="Zadajte názov podkategórie" @keyup.enter="addSubcategory" />
                                <button type="button" @click="addSubcategory"
                                    class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                                    Pridať
                                </button>
                            </div> <!-- List of subcategories -->
                            <div v-if="priceCategoryForm.subcategories && priceCategoryForm.subcategories.length > 0"
                                class="mt-3 space-y-2">
                                <div v-for="(subcategory, index) in priceCategoryForm.subcategories" :key="index"
                                    :class="[
                                        'flex items-center justify-between px-3 py-2 rounded-md',
                                        editingSubcategoryIndex === index ? 'bg-teal-50 border border-teal-200' : 'bg-gray-50'
                                    ]">
                                    <!-- Display mode -->
                                    <span v-if="editingSubcategoryIndex !== index" class="text-sm text-gray-700">{{
                                        subcategory }}</span>
                                    <!-- Edit mode -->
                                    <input v-else v-model="editingSubcategoryValue" type="text"
                                        class="flex-1 mr-2 border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                        @keyup.enter="saveSubcategoryEdit(index)" @keyup.escape="cancelSubcategoryEdit"
                                        :ref="el => { if (el && editingSubcategoryIndex === index) (el as HTMLInputElement).focus() }" />

                                    <!-- Action buttons -->
                                    <div class="flex items-center space-x-1">
                                        <template v-if="editingSubcategoryIndex !== index">
                                            <!-- Edit button -->
                                            <button type="button" @click="startEditSubcategory(index, subcategory)"
                                                class="text-teal-600 hover:text-teal-800 p-1">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                                                    </path>
                                                </svg>
                                            </button>
                                            <!-- Delete button -->
                                            <button type="button" @click="removeSubcategory(index)"
                                                class="text-red-600 hover:text-red-800 p-1">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                                </svg>
                                            </button>
                                        </template>
                                        <template v-else>
                                            <!-- Save button -->
                                            <button type="button" @click="saveSubcategoryEdit(index)"
                                                class="text-green-600 hover:text-green-800 p-1">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M5 13l4 4L19 7"></path>
                                                </svg>
                                            </button>
                                            <!-- Cancel button -->
                                            <button type="button" @click="cancelSubcategoryEdit"
                                                class="text-gray-600 hover:text-gray-800 p-1">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                                </svg>
                                            </button>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="flex justify-end space-x-3 pt-4">
                            <button type="button" @click="closeModal"
                                class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                                Zrušiť
                            </button>
                            <button type="submit" :disabled="isSubmitting"
                                class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50">
                                {{ isSubmitting ? 'Ukladá sa...' : (editMode ? 'Uložiť' : 'Vytvoriť') }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from 'vue'
import PriceCategoryService from '@/services/PriceCategoryService'
import type { PriceCategory, CreatePriceCategoryRequest, UpdatePriceCategoryRequest } from '@/services/PriceCategoryService'

// Component state
const loading = ref(false)
const error = ref<string | null>(null)
const showModal = ref(false)
const editMode = ref(false)
const isSubmitting = ref(false)

// Data
const priceCategories = ref<PriceCategory[]>([])
const newSubcategory = ref('')
const editingCategoryId = ref<number | null>(null)

// Subcategory editing state
const editingSubcategoryIndex = ref<number | null>(null)
const editingSubcategoryValue = ref('')

// Form data
const priceCategoryForm = ref<CreatePriceCategoryRequest>({
    name: '',
    description: '',
    subcategories: [],
    is_active: true,
    sort_order: 0
})

// Computed properties
const priceCategoriesWithDetails = computed(() => {
    return priceCategories.value.map(category => ({
        id: category.id,
        name: category.name,
        description: category.description,
        priceCount: category.price_count,
        subcategories: category.subcategories || [],
        is_active: category.is_active,
        sort_order: category.sort_order
    })).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
})

// Load data on mount
onMounted(() => {
    fetchPriceCategories()
})

// Fetch all price categories
async function fetchPriceCategories() {
    loading.value = true
    error.value = null

    try {
        const response = await PriceCategoryService.getAllCategories()
        const data = response.data.data || response.data
        priceCategories.value = Array.isArray(data) ? data : []
    } catch (err) {
        console.error('Error loading price categories:', err)
        error.value = 'Nepodarilo sa načítať kategórie cien. Skúste to znovu.'
    } finally {
        loading.value = false
    }
}

// Modal handlers
function handleAddPriceCategory() {
    editMode.value = false
    editingCategoryId.value = null
    priceCategoryForm.value = {
        name: '',
        description: '',
        subcategories: [],
        is_active: true,
        sort_order: 0
    }
    newSubcategory.value = ''
    // Cancel any ongoing subcategory editing
    cancelSubcategoryEdit()
    showModal.value = true
}

function handleEditPriceCategory(priceCategory: { id: number; name: string; description?: string; subcategories: string[]; is_active: boolean; sort_order: number }) {
    editMode.value = true
    editingCategoryId.value = priceCategory.id
    priceCategoryForm.value = {
        name: priceCategory.name,
        description: priceCategory.description || '',
        subcategories: [...(priceCategory.subcategories || [])],
        is_active: priceCategory.is_active,
        sort_order: priceCategory.sort_order
    }
    newSubcategory.value = ''
    // Cancel any ongoing subcategory editing
    cancelSubcategoryEdit()
    showModal.value = true
}

function closeModal() {
    showModal.value = false
    priceCategoryForm.value = {
        name: '',
        description: '',
        subcategories: [],
        is_active: true,
        sort_order: 0
    }
    newSubcategory.value = ''
    editingCategoryId.value = null
    error.value = null
    // Cancel any ongoing subcategory editing
    cancelSubcategoryEdit()
}

// Subcategory management
function addSubcategory() {
    if (newSubcategory.value.trim() && !priceCategoryForm.value.subcategories?.includes(newSubcategory.value.trim())) {
        if (!priceCategoryForm.value.subcategories) {
            priceCategoryForm.value.subcategories = []
        }
        priceCategoryForm.value.subcategories.push(newSubcategory.value.trim())
        newSubcategory.value = ''
    }
}

function removeSubcategory(index: number) {
    if (priceCategoryForm.value.subcategories) {
        priceCategoryForm.value.subcategories.splice(index, 1)
    }
}

// Subcategory editing
async function startEditSubcategory(index: number, subcategory: string) {
    editingSubcategoryIndex.value = index
    editingSubcategoryValue.value = subcategory

    // Focus the input field after Vue updates the DOM
    await nextTick()
}

function saveSubcategoryEdit(index: number) {
    if (editingSubcategoryValue.value.trim() && priceCategoryForm.value.subcategories) {
        // Check for duplicates (excluding the current one being edited)
        const trimmedValue = editingSubcategoryValue.value.trim()
        const isDuplicate = priceCategoryForm.value.subcategories.some((sub, i) =>
            i !== index && sub === trimmedValue
        )

        if (!isDuplicate) {
            priceCategoryForm.value.subcategories[index] = trimmedValue
            cancelSubcategoryEdit()
        } else {
            // Could show a toast or error message here
            alert('Podkategória s týmto názvom už existuje')
        }
    }
}

function cancelSubcategoryEdit() {
    editingSubcategoryIndex.value = null
    editingSubcategoryValue.value = ''
}

// Form submission
async function handleSubmit() {
    if (!priceCategoryForm.value.name?.trim()) return

    isSubmitting.value = true
    error.value = null

    try {
        if (editMode.value && editingCategoryId.value) {
            // Update existing category
            const updateData: UpdatePriceCategoryRequest = {
                name: priceCategoryForm.value.name,
                description: priceCategoryForm.value.description,
                subcategories: priceCategoryForm.value.subcategories,
                is_active: priceCategoryForm.value.is_active,
                sort_order: priceCategoryForm.value.sort_order
            }
            await PriceCategoryService.updateCategory(editingCategoryId.value, updateData)
        } else {
            // Create new category
            await PriceCategoryService.createCategory(priceCategoryForm.value)
        }

        await fetchPriceCategories() // Refresh data
        closeModal()
    } catch (err: unknown) {
        console.error('Error saving category:', err)
        if (err && typeof err === 'object' && 'response' in err) {
            const response = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response
            if (response?.data?.message) {
                error.value = response.data.message
            } else if (response?.data?.errors) {
                // Handle validation errors
                const errorMessages: string[] = []
                for (const field in response.data.errors) {
                    errorMessages.push(...response.data.errors[field])
                }
                error.value = errorMessages.join(', ')
            } else {
                error.value = 'Nepodarilo sa uložiť kategóriu. Skúste to znovu.'
            }
        } else {
            error.value = 'Nepodarilo sa uložiť kategóriu. Skúste to znovu.'
        }
    } finally {
        isSubmitting.value = false
    }
}

async function handleDeletePriceCategory(priceCategory: { id: number; name: string; priceCount: number }) {
    if (priceCategory.priceCount > 0) {
        alert('Nie je možné vymazať kategóriu, ktorá obsahuje ceny. Najprv vymaže alebo presuňte všetky ceny.')
        return
    }

    if (confirm(`Naozaj chcete vymazať kategóriu "${priceCategory.name}"?`)) {
        try {
            await PriceCategoryService.deleteCategory(priceCategory.id)
            await fetchPriceCategories() // Refresh data
        } catch (err: unknown) {
            console.error('Error deleting category:', err)
            if (err && typeof err === 'object' && 'response' in err) {
                const response = (err as { response?: { data?: { message?: string } } }).response
                if (response?.data?.message) {
                    error.value = response.data.message
                } else {
                    error.value = 'Nepodarilo sa vymazať kategóriu.'
                }
            } else {
                error.value = 'Nepodarilo sa vymazať kategóriu.'
            }
        }
    }
}
</script>
